import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CloseIcon from "@mui/icons-material/Close";
import CarFormDialog from "./CarFormDialog";
import DriverSelectDialog from "./DriverSelectDialog";

function CarSelectDialog({
  open,
  onClose,
  onSuccess,
  customerData,
  fetchData,
}) {
  const [openAddCarDialog, setOpenAddCarDialog] = useState(false);
  const [openDriverDialog, setOpenDriverDialog] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);

  // สร้าง state local สำหรับ customerData
  const [localCustomerData, setLocalCustomerData] = useState(customerData);

  // อัปเดต localCustomerData เวลา customerData prop เปลี่ยน
  useEffect(() => {
    setLocalCustomerData(customerData);
  }, [customerData]);

  const handleCarClick = (car) => {
    setSelectedCar(car);
    setOpenDriverDialog(true);
  };

  const handleCarSkip = () => {
    onSuccess &&
      onSuccess({
        car: [],
        driver: [],
        customer: localCustomerData,
      });
    onClose && onClose();
  };

  const handleDriverPicked = (driver) => {
    onSuccess &&
      onSuccess({
        car: selectedCar,
        driver,
        customer: localCustomerData,
      });

    setOpenDriverDialog(false);
    onClose && onClose();
  };

  // ฟังก์ชัน fetch และอัปเดต localCustomerData
  const refreshCustomerData = async () => {
    if (!fetchData) return;

    const data = await fetchData();
    if (!Array.isArray(data)) return;

    const updatedCustomer = data.find((c) => c.id === localCustomerData?.id);
    if (updatedCustomer) setLocalCustomerData(updatedCustomer);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            height: "45vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle className="bg-white border-b border-gray-100 p-4 sm:p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <span className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                <DirectionsCarIcon />
              </span>
              เลือกรถของ {localCustomerData?.name || "-"}
            </h2>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={() => setOpenAddCarDialog(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-green-50 text-green-700 hover:bg-green-100 rounded-xl font-medium transition-all text-sm w-full sm:w-auto"
            >
              <AddIcon fontSize="small" />
              เพิ่มรถใหม่
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-100 rounded-full transition-colors sm:block hidden">
                <CloseIcon />
            </button>
          </div>
        </DialogTitle>

        <DialogContent className="bg-slate-50/50 p-6 overflow-y-auto custom-scrollbar">
          {localCustomerData?.cars?.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-2">
              {localCustomerData.cars.map((car) => {
                 const isTruck = (car.type || "").includes("บรรทุก") || (car.type || "").includes("กระบะ");
                 return (
                    <div
                      key={car.id}
                      onClick={() => handleCarClick(car)}
                      className="group bg-white border border-gray-200 rounded-2xl p-5 hover:border-blue-300 hover:shadow-lg hover:shadow-blue-500/5 cursor-pointer transition-all duration-200 flex flex-col gap-3 relative overflow-hidden"
                    >
                      <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-gray-50 to-white rounded-bl-full -mr-10 -mt-10 transition-transform group-hover:scale-110 z-0`}></div>
                      
                      <div className="relative z-10 flex items-start justify-between">
                         <div className={`p-3 rounded-xl ${isTruck ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'}`}>
                            {isTruck ? <LocalShippingIcon /> : <DirectionsCarIcon />}
                         </div>
                      </div>

                      <div className="relative z-10">
                        <h3 className="font-bold text-lg text-gray-800 mb-1">
                          {car.license || "-"}
                        </h3>
                        <p className="text-sm text-gray-500 font-medium">
                          {car.province || "-"}
                        </p>
                      </div>

                      <div className="relative z-10 mt-auto pt-3 border-t border-gray-50">
                         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {car.type || "รถทั่วไป"}
                         </span>
                      </div>
                    </div>
                );
              })}
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-gray-400 min-h-[300px]">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <DirectionsCarIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                </div>
                <p className="text-lg font-medium text-gray-500">ยังไม่มีข้อมูลรถ</p>
                <p className="text-sm">กดปุ่ม "เพิ่มรถใหม่" เพื่อเริ่มต้นใช้งาน</p>
            </div>
          )}
        </DialogContent>

        <DialogActions className="bg-white border-t border-gray-100 p-4 sm:px-6 flex justify-between">
          <button
            onClick={handleCarSkip}
            className="px-6 py-2.5 rounded-xl text-gray-500 font-medium hover:bg-gray-50 hover:text-gray-700 transition-all text-sm"
          >
            ข้ามขั้นตอนนี้
          </button>
          
          <button
            onClick={onClose}
            className="px-8 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition-all text-sm"
          >
            ยกเลิก
          </button>
        </DialogActions>
      </Dialog>

      {/* ฟอร์มเพิ่มรถ */}
      <CarFormDialog
        open={openAddCarDialog}
        onClose={() => setOpenAddCarDialog(false)}
        customerData={localCustomerData}
        onSuccess={async () => {
          setOpenAddCarDialog(false);
          await refreshCustomerData(); // อัปเดตหลังเพิ่มรถ
        }}
      />

      {/* เลือกคนขับ */}
      <DriverSelectDialog
        open={openDriverDialog}
        onClose={() => setOpenDriverDialog(false)}
        customerData={localCustomerData} // ใช้ localCustomerData
        carData={selectedCar}
        onPick={handleDriverPicked}
        onSuccess={async () => {
          await refreshCustomerData(); // อัปเดตหลังเลือกคนขับ
        }}
      />
    </>
  );
}

export default CarSelectDialog;
